import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  Button,
  Table,
  Spinner,
  Badge,
} from "reactstrap";
import api from "../../api";
import coinImg from "../../assets/images/items/coin.png";
import "./BoxRater.css";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

const VARIANTS = [
  "Diamond",
  "Golden",
  "Ruby",
  "Sapphire",
  "Emerald",
  "Shiny",
  "Shadow",
  "Normal",
];

const VARIANT_COLORS = {
  Diamond: "#4dd0e1",
  Golden: "#fbc02d",
  Ruby: "#e57373",
  Sapphire: "#64b5f6",
  Emerald: "#81c784",
  Shiny: "#b39ddb",
  Shadow: "#37474f",
  Normal: "#ffffff",
  Total: "#ff9800",
};

// Type color mapping (aligned to dark theme)
const TYPE_COLORS = {
  normal: "#A8A77A",
  fire: "#EE8130",
  water: "#6390F0",
  electric: "#F7D02C",
  grass: "#7AC74C",
  ice: "#96D9D6",
  fighting: "#C22E28",
  poison: "#A33EA1",
  ground: "#E2BF65",
  flying: "#A98FF3",
  psychic: "#F95587",
  bug: "#A6B91A",
  rock: "#B6A136",
  ghost: "#735797",
  dragon: "#6F35FC",
  dark: "#705746",
  steel: "#B7B7CE",
  fairy: "#D685AD",
};

function formatCoins(n) {
  if (typeof n !== "number") return n;
  return n.toLocaleString();
}

function formatShort(n) {
  if (typeof n !== "number") return n;
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000_000) {
    const v = n / 1_000_000_000_000;
    const s = Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1);
    return `${s}t`;
  }
  if (abs >= 1_000_000_000) {
    const v = n / 1_000_000_000;
    const s = Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1);
    return `${s}b`;
  }
  if (abs >= 1_000_000) {
    const v = n / 1_000_000;
    const s = Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1);
    return `${s}m`;
  }
  if (abs >= 1_000) {
    const v = n / 1_000;
    const s = Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1);
    return `${s}k`;
  }
  return n.toLocaleString();
}

// Shared rarity formatter
function formatRarity(r) {
  if (!r) return "0 (0M/0F/0G)";
  const t = Number(r.total || 0);
  const m = Number(r.male || 0);
  const f = Number(r.female || 0);
  const g = Number(r.ungendered || 0);
  return `${t.toLocaleString()} (${m.toLocaleString()}M/${f.toLocaleString()}F/${g.toLocaleString()}G)`;
}

function CurrentView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [variant, setVariant] = useState("All");
  const [search, setSearch] = useState("");

  const fetchData = async (opts = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      const v = (opts.variant ?? variant);
      const q = (opts.search ?? search).trim();
      if (v && v !== "All") params.variant = v;
      if (q) params.search = q;
      const res = await api.get("/v1/rate-list", { params, withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error(e);
      setError("Failed to load rate list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const percentRow = useMemo(() => {
    if (!data) return null;
    const totalValSafe = Math.max(1, data.total_value || 0);
    const row = { name: "Share" };
    VARIANTS.forEach((v) => {
      row[v] = ((data.variants?.[v]?.value || 0) / totalValSafe) * 100;
    });
    return row;
  }, [data]);

  const grouped = useMemo(() => {
    if (!data) return {};
    const g = {};
    for (const e of (data.entries || [])) {
      const v = e.variant || "Normal";
      if (!g[v]) g[v] = [];
      g[v].push(e);
    }
    Object.keys(g).forEach((k) => g[k].sort((a, b) => (b.price_value || 0) - (a.price_value || 0)));
    return g;
  }, [data]);

  return (
    <>
      <Row className="mb-3">
        <Col xs={12} md="auto" className="mb-2">
          <Input
            type="select"
            className="boxrater-input"
            value={variant}
            onChange={(e) => setVariant(e.target.value)}
          >
            <option>All</option>
            {VARIANTS.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </Input>
        </Col>
        <Col xs={12} md={6} lg={5} xl={4} className="mb-2">
          <Input
            className="boxrater-input"
            placeholder="Search species..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") fetchData(); }}
          />
        </Col>
        <Col xs="auto" className="mb-2">
          <Button color="primary" onClick={() => fetchData()} disabled={loading}>
            {loading ? "Loading..." : "Apply"}
          </Button>
        </Col>
      </Row>

      {loading && <Spinner color="light" />}
      {error && <div className="text-danger">{error}</div>}

      {data && (
        <>
          <Row className="mb-3">
            <Col md={12}>
              <Card className="boxrater-card">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-2 text-white">Totals</h5>
                    <Badge color="info">Current</Badge>
                  </div>
                  <div className="small boxrater-overview">Global rate list overview</div>
                  <div className="mt-2 boxrater-emphasis">Entries: <strong>{formatCoins(data.count)}</strong></div>
                  <div className="boxrater-emphasis">Aggregate Value: <img src={coinImg} alt="coin" className="boxrater-coin" /><strong>{formatShort(data.total_value)}</strong></div>

                  <div className="mt-3 boxrater-sharebar">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <small className="text-muted">Variant distribution</small>
                    </div>
                    <div style={{ width: "100%", height: 36 }}>
                      <ResponsiveContainer>
                        <BarChart data={[percentRow]} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 0 }}>
                          <XAxis type="number" domain={[0, 100]} hide tickFormatter={(v) => `${Math.round(v)}%`} />
                          <YAxis type="category" dataKey="name" hide />
                          <Tooltip formatter={(v, name) => [`${(typeof v === 'number' ? v : 0).toFixed(1)}%`, name]} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          {VARIANTS.map((v) => (
                            <Bar key={v} dataKey={v} stackId="share" fill={VARIANT_COLORS[v]} />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            {VARIANTS.map((v) => {
              const list = grouped[v] || [];
              if (!list.length) return null;
              return (
                <Col md={12} key={v} className="mb-3">
                  <Card className="boxrater-card boxrater-variant-card" style={{ "--variant-color": VARIANT_COLORS[v] }}>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-3 text-white">{v}</h6>
                        <Badge color="secondary">{list.length} items</Badge>
                      </div>
                      <Table responsive striped dark size="sm" className="boxrater-table">
                        <thead>
                          <tr>
                            <th>Pokémon</th>
                            <th className="text-end">Rarity</th>
                            <th className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.map((e, idx) => (
                            <tr key={idx}>
                              <td>
                                <div style={{ fontWeight: 600, color: "#fff" }}>{e.poke_name}</div>
                           
                              </td>
                              <td className="text-end">{formatRarity(e.rarity)}</td>
                              <td className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(e.price_value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      )}
    </>
  );
}

function HistoryView() {
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSeries, setSelectedSeries] = useState(() => new Set(["Total"]));
  const [species, setSpecies] = useState("");

  const fetchData = async (w, s) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ weeks: String(w), species: s || "" }).toString();
      const res = await api.get(`/v1/rate-list/history/species?${params}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error(e);
      setError("Failed to load history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((species || "").trim()) {
      fetchData(weeks, species);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const byWeek = {};
    for (const v of Object.keys(data.series || {})) {
      for (const p of data.series[v]) {
        const wk = p.week;
        if (!byWeek[wk]) byWeek[wk] = { week: wk };
        byWeek[wk][v] = (byWeek[wk][v] || 0) + (p.value || 0);
      }
    }
    for (const t of data.totals || []) {
      const wk = t.week;
      if (!byWeek[wk]) byWeek[wk] = { week: wk };
      byWeek[wk].Total = t.value || 0;
    }
    return Object.values(byWeek).sort((a, b) => a.week.localeCompare(b.week));
  }, [data]);

  const toggleSeries = (key) => {
    setSelectedSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      if (next.size === 0) next.add("Total");
      return next;
    });
  };
  const selectAll = () => setSelectedSeries(new Set(["Total", ...VARIANTS]));
  const clearAll = () => setSelectedSeries(new Set(["Total"]));

  const GrowthTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0]?.payload || {};
    return (
      <div className="recharts-default-tooltip">
        <div><strong>Week: {label}</strong></div>
        <div>Total: <img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(d.Total || 0)}</div>
        {VARIANTS.map((v) => (
          <div key={v}>{v}: <img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(d[v] || 0)}</div>
        ))}
      </div>
    );
  };

  return (
    <>
      <Row className="mb-3">
        <Col xs={12} md={6} lg={4} className="mb-2">
          <Input
            className="boxrater-input"
            placeholder="Enter species (e.g., Charizard)"
            value={species}
            onChange={(e) => setSpecies(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") fetchData(weeks, species); }}
          />
        </Col>
        <Col xs="auto" className="mb-2">
          <Button size="sm" color="primary" onClick={() => fetchData(weeks, species)} disabled={!species || loading}>
            {loading ? "Loading..." : "Search"}
          </Button>
        </Col>
        <Col md="auto">
          <label className="me-2 text-white">Range:</label>
          <Button size="sm" color={weeks === 4 ? "primary" : "secondary"} className="me-1" onClick={() => setWeeks(4)}>4w</Button>
          <Button size="sm" color={weeks === 12 ? "primary" : "secondary"} className="me-1" onClick={() => setWeeks(12)}>12w</Button>
          <Button size="sm" color={weeks === 24 ? "primary" : "secondary"} className="me-1" onClick={() => setWeeks(24)}>24w</Button>
          <Button size="sm" color={weeks === 52 ? "primary" : "secondary"} onClick={() => setWeeks(52)}>52w</Button>
        </Col>
      </Row>

      {loading && <Spinner color="light" />}
      {error && <div className="text-danger">{error}</div>}

      {!loading && data && (
        <Row>
          <Col md={12} className="mb-2">
            <div className="d-flex flex-wrap align-items-center">
              <label className="me-2 mb-1 text-white">Series:</label>
              <Button size="sm" color={selectedSeries.has("Total") ? "primary" : "secondary"} className="me-1 mb-1" onClick={() => toggleSeries("Total")}>
                Total
              </Button>
              {VARIANTS.map((v) => (
                <Button
                  key={v}
                  size="sm"
                  outline
                  className="me-1 mb-1"
                  style={{ borderColor: VARIANT_COLORS[v], color: selectedSeries.has(v) ? "#fff" : VARIANT_COLORS[v], background: selectedSeries.has(v) ? "rgba(255,255,255,0.08)" : "transparent" }}
                  color={selectedSeries.has(v) ? "primary" : "secondary"}
                  onClick={() => toggleSeries(v)}
                >
                  {v}
                </Button>
              ))}
              <Button size="sm" color="secondary" className="ms-1 mb-1" onClick={selectAll}>All</Button>
              <Button size="sm" color="secondary" className="ms-1 mb-1" onClick={clearAll}>None</Button>
            </div>
          </Col>
          <Col md={12} className="mb-3">
            <Card className="boxrater-card">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-3 text-white">History for: {species || '—'}</h6>
                  <Badge color="warning">Species</Badge>
                </div>
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#3f4853" />
                      <XAxis dataKey="week" tick={{ fill: '#e8eaed', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#e8eaed', fontSize: 12 }} tickFormatter={(v) => formatShort(v)} width={80} />
                      <Tooltip content={<GrowthTooltip />} />
                      <Legend />
                      {[...selectedSeries]
                        .sort((a, b) => (a === "Total" ? -1 : b === "Total" ? 1 : VARIANTS.indexOf(a) - VARIANTS.indexOf(b)))
                        .map((key) => (
                          <Line
                            type="monotone"
                            key={key}
                            dataKey={key}
                            stroke={VARIANT_COLORS[key] || '#90caf9'}
                            dot={{ r: 2 }}
                            strokeWidth={2}
                          />
                        ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md={12}>
            <Card className="boxrater-card">
              <CardBody>
                <h6 className="mb-3 text-white">Per Variant (latest week)</h6>
                <Table responsive striped dark className="boxrater-table">
                  <thead>
                    <tr>
                      <th>Variant</th>
                      <th className="text-end">Rarity</th>
                      <th className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const keys = Object.keys(data.series || {});
                      if (!keys.length) return null;
                      let lastWeek = null;
                      for (const v of keys) {
                        const arr = data.series[v];
                        if (arr && arr.length) {
                          const wk = arr[arr.length - 1].week;
                          if (!lastWeek || wk > lastWeek) lastWeek = wk;
                        }
                      }
                      return VARIANTS.map((v) => {
                        const arr = data.series[v] || [];
                        const rec = (arr || []).find((p) => p.week === lastWeek) || { count: 0, value: 0, rarity: { total: 0, male: 0, female: 0, ungendered: 0 } };
                        return (
                          <tr key={v}>
                            <td>{v}</td>
                            <td className="text-end">{formatRarity(rec.rarity)}</td>
                            <td className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(rec.value || 0)}</td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
}

export default function RateList() {
  const [activeTab, setActiveTab] = useState("current");

  useEffect(() => {
    document.title = "Rate List";
  }, []);

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col md={12}>
            <Card className="boxrater-card">
              <CardBody>
                <h4 className="mb-3">Rate List</h4>
                <Nav tabs>
                  <NavItem>
                    <NavLink
                      className={activeTab === "current" ? "active" : ""}
                      onClick={() => setActiveTab("current")}
                      style={{ cursor: "pointer" }}
                    >
                      Current
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "history" ? "active" : ""}
                      onClick={() => setActiveTab("history")}
                      style={{ cursor: "pointer" }}
                    >
                      History
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="pt-3">
                  <TabPane tabId="current">
                    <CurrentView />
                  </TabPane>
                  <TabPane tabId="history">
                    <HistoryView />
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
